<template>
  <a
    v-if="isNativeLink"
    :href="to"
    :target="linkTarget"
    :download="downloadAttr"
    :rel="isExternalLink ? 'noopener nofollow' : ''"
    @click="onLinkClick"
  >
    <slot />
  </a>

  <NuxtLink
    v-else
    v-slot="{ href, navigate, isActive, isExactActive }"
    :to="localePath(to ?? '')"
    custom
  >
    <a
      :href="href"
      :target="linkTarget"
      :aria-current="isExactActive ? 'page' : undefined"
      :class="{
        'nuxt-link-active': isActive,
        'nuxt-link-exact-active': isExactActive,
      }"
      @click="onRouterLinkClick($event, navigate)"
    >
      <slot />
    </a>
  </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  to?: string;
  target?: string;
  download?: string | boolean;
}

interface Emits {
  (e: 'click'): void;
}

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  target: undefined,
  download: false,
});

const emit = defineEmits<Emits>();

const localePath = useLocalePath();

const isExternalLink = computed(() => /^https?:/.test(props.to ?? ''));
const isSamePageLink = computed(() => props.to?.startsWith('#'));

const isNativeLink = computed(
  () => isExternalLink.value || isSamePageLink.value,
);

const downloadAttr = computed(() =>
  props.download === true ? '' : props.download || undefined,
);

const linkTarget = computed(
  () => props.target ?? (isExternalLink.value ? '_blank' : '_self'),
);

const onLinkClick = (e: MouseEvent) => {
  if (isSamePageLink.value) {
    e.preventDefault();
    if (props.to)
      document?.querySelector(props.to)?.scrollIntoView({
        behavior: 'smooth',
      });
  }

  emit('click');
};

const onRouterLinkClick = (
  e: MouseEvent,
  navigate: (event: MouseEvent) => void,
) => {
  navigate(e);
  onLinkClick(e);
};
</script>
